<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers, url } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useToggle } from '@vueuse/core';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';
import { getAssignmentRules } from '../assignmentRules';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
  crmIntegrationAvailable: {
    type: Boolean,
    default: false,
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');

const formState = reactive({
  botName: '',
  botDescription: '',
  botUrl: '',
  botAvatar: null,
  botAvatarUrl: '',
  automaticDeliveryEnabled: true,
  assignmentRules: getAssignmentRules(),
  crmEnabled: false,
  crmCreateLeads: true,
  crmReassignExisting: true,
  crmSyncSummary: true,
  crmReassignUnattended: true,
  crmVtigerOwnerAuthoritative: false,
  crmInitialDeliveryOnly: false,
});

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');
const botSecret = ref('');

const v$ = useVuelidate(
  {
    botName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
        required
      ),
    },
    botUrl: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.URL'),
        required
      ),
      url: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.VALID_URL'),
        url
      ),
    },
  },
  formState
);

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating
    : uiFlags.value.isUpdating
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  }

  return props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION');
  }
  return '';
});

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const botUrlError = computed(() =>
  v$.value.botUrl.$error ? v$.value.botUrl.$errors[0]?.$message : ''
);

const configuredBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  return value === true || value === 'true' || value === 1 || value === '1';
};

const showAccessTokenInput = computed(
  () =>
    showAccessToken.value ||
    props.type === MODAL_TYPES.EDIT ||
    accessToken.value
);

const resetForm = () => {
  Object.assign(formState, {
    botName: '',
    botDescription: '',
    botUrl: '',
    botAvatar: null,
    botAvatarUrl: '',
    automaticDeliveryEnabled: true,
    assignmentRules: getAssignmentRules(),
    crmEnabled: false,
    crmCreateLeads: true,
    crmReassignExisting: true,
    crmSyncSummary: true,
    crmReassignUnattended: true,
    crmVtigerOwnerAuthoritative: false,
    crmInitialDeliveryOnly: false,
  });
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const moveAssignmentRule = (index, direction) => {
  const nextIndex = index + direction;
  if (
    index < 3 ||
    nextIndex < 3 ||
    nextIndex >= formState.assignmentRules.length
  ) {
    return;
  }

  const rules = [...formState.assignmentRules];
  [rules[index], rules[nextIndex]] = [rules[nextIndex], rules[index]];
  formState.assignmentRules = rules;
};

const assignmentRuleLabels = computed(() => ({
  available_agent: t('AGENT_BOTS.ASSIGNMENT_RULES.RULES.available_agent.LABEL'),
  fair_rotation: t('AGENT_BOTS.ASSIGNMENT_RULES.RULES.fair_rotation.LABEL'),
  previous_contact_agent: t(
    'AGENT_BOTS.ASSIGNMENT_RULES.RULES.previous_contact_agent.LABEL'
  ),
  active_load: t('AGENT_BOTS.ASSIGNMENT_RULES.RULES.active_load.LABEL'),
  resolved_conversations: t(
    'AGENT_BOTS.ASSIGNMENT_RULES.RULES.resolved_conversations.LABEL'
  ),
  first_response_time: t(
    'AGENT_BOTS.ASSIGNMENT_RULES.RULES.first_response_time.LABEL'
  ),
}));

const assignmentRuleDescriptions = computed(() => ({
  available_agent: t(
    'AGENT_BOTS.ASSIGNMENT_RULES.RULES.available_agent.DESCRIPTION'
  ),
  fair_rotation: t(
    'AGENT_BOTS.ASSIGNMENT_RULES.RULES.fair_rotation.DESCRIPTION'
  ),
  previous_contact_agent: t(
    'AGENT_BOTS.ASSIGNMENT_RULES.RULES.previous_contact_agent.DESCRIPTION'
  ),
  active_load: t('AGENT_BOTS.ASSIGNMENT_RULES.RULES.active_load.DESCRIPTION'),
  resolved_conversations: t(
    'AGENT_BOTS.ASSIGNMENT_RULES.RULES.resolved_conversations.DESCRIPTION'
  ),
  first_response_time: t(
    'AGENT_BOTS.ASSIGNMENT_RULES.RULES.first_response_time.DESCRIPTION'
  ),
}));

const assignmentRuleLabel = rule => assignmentRuleLabels.value[rule];

const assignmentRuleDescription = rule =>
  assignmentRuleDescriptions.value[rule];

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (showAccessToken.value) return;

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    outgoing_url: formState.botUrl,
    bot_type: 'webhook',
    avatar: formState.botAvatar,
    automatic_delivery: {
      enabled: formState.automaticDeliveryEnabled,
    },
    assignment_rules: formState.assignmentRules,
    crm: {
      enabled: formState.crmEnabled,
      create_leads: formState.crmCreateLeads,
      reassign_existing: formState.crmReassignExisting,
      sync_summary: formState.crmSyncSummary,
      reassign_unattended: formState.crmReassignUnattended,
      vtiger_owner_authoritative: formState.crmVtigerOwnerAuthoritative,
      initial_delivery_only: formState.crmInitialDeliveryOnly,
    },
  };

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };

    const response = await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    const alertKey = isCreate
      ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    // Show access token and secret after creation
    if (isCreate) {
      const {
        access_token: responseAccessToken,
        secret: responseSecret,
        id,
      } = response || {};

      if (id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        botSecret.value = responseSecret || '';
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        botSecret.value = '';
        dialogRef.value.close();
      }
    } else {
      dialogRef.value.close();
    }

    resetForm();
  } catch (error) {
    const errorKey = isCreate
      ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      outgoing_url: botUrl,
      thumbnail,
      bot_config: botConfig,
      access_token: botAccessToken,
      secret: botSecretValue,
    } = props.selectedBot;
    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.botUrl = botUrl || botConfig?.webhook_url || '';
    formState.botAvatarUrl = thumbnail || '';
    formState.automaticDeliveryEnabled = configuredBoolean(
      botConfig?.automatic_delivery?.enabled,
      true
    );
    formState.assignmentRules = getAssignmentRules(botConfig);
    formState.crmEnabled =
      props.crmIntegrationAvailable &&
      configuredBoolean(botConfig?.crm?.enabled);
    formState.crmCreateLeads = configuredBoolean(
      botConfig?.crm?.create_leads,
      true
    );
    formState.crmReassignExisting = configuredBoolean(
      botConfig?.crm?.reassign_existing,
      true
    );
    formState.crmSyncSummary = configuredBoolean(
      botConfig?.crm?.sync_summary,
      true
    );
    formState.crmReassignUnattended = configuredBoolean(
      botConfig?.crm?.reassign_unattended,
      true
    );
    formState.crmVtigerOwnerAuthoritative = configuredBoolean(
      botConfig?.crm?.vtiger_owner_authoritative
    );
    formState.crmInitialDeliveryOnly = configuredBoolean(
      botConfig?.crm?.initial_delivery_only
    );

    if (props.type === MODAL_TYPES.EDIT) {
      if (botAccessToken) accessToken.value = botAccessToken;
      if (botSecretValue) botSecret.value = botSecretValue;
    }
  } else {
    resetForm();
  }
};

const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
};

const onCopySecret = async value => {
  await copyTextToClipboard(value || botSecret.value);
  useAlert(t('AGENT_BOTS.SECRET.COPY_SUCCESS'));
};

const onResetSecret = async () => {
  const response = await store.dispatch(
    'agentBots/resetSecret',
    props.selectedBot.id
  );
  if (response) {
    botSecret.value = response.secret;
    useAlert(t('AGENT_BOTS.SECRET.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.SECRET.RESET_ERROR'));
  }
};

const onResetToken = async () => {
  const response = await store.dispatch(
    'agentBots/resetAccessToken',
    props.selectedBot.id
  );
  if (response) {
    accessToken.value = response.access_token;
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
  }
};

const closeModal = () => {
  if (!showAccessToken.value) v$.value?.$reset();
  accessToken.value = '';
  botSecret.value = '';
  toggleAccessToken(false);
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    overflow-y-auto
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="closeModal"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <div
        v-if="!showAccessToken || type === MODAL_TYPES.EDIT"
        class="flex flex-col gap-4"
      >
        <div class="mb-2 flex flex-col items-start">
          <span class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
          </span>
          <Avatar
            :src="formState.botAvatarUrl"
            :name="formState.botName"
            :size="68"
            allow-upload
            icon-name="i-lucide-bot-message-square"
            @upload="handleImageUpload"
            @delete="handleAvatarDelete"
          />
        </div>

        <Input
          id="bot-name"
          v-model="formState.botName"
          :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
          :message="botNameError"
          :message-type="botNameError ? 'error' : 'info'"
          @blur="v$.botName.$touch()"
        />

        <TextArea
          id="bot-description"
          v-model="formState.botDescription"
          :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
        />

        <Input
          id="bot-url"
          v-model="formState.botUrl"
          :label="$t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')"
          :message="botUrlError"
          :message-type="botUrlError ? 'error' : 'info'"
          @blur="v$.botUrl.$touch()"
        />

        <div class="flex flex-col gap-3 rounded-xl border border-n-weak p-4">
          <div>
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.AUTOMATIC_DELIVERY.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('AGENT_BOTS.AUTOMATIC_DELIVERY.DESCRIPTION') }}
            </p>
          </div>
          <label class="flex items-start gap-3">
            <Checkbox v-model="formState.automaticDeliveryEnabled" />
            <span class="block text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.AUTOMATIC_DELIVERY.ENABLED') }}
            </span>
          </label>
        </div>

        <div class="flex flex-col gap-3 rounded-xl border border-n-weak p-4">
          <div>
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.ASSIGNMENT_RULES.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('AGENT_BOTS.ASSIGNMENT_RULES.DESCRIPTION') }}
            </p>
          </div>

          <div
            v-for="(rule, index) in formState.assignmentRules"
            :key="rule"
            class="flex items-center gap-3 rounded-lg bg-n-alpha-2 p-3"
          >
            <span
              class="flex size-7 shrink-0 items-center justify-center rounded-full bg-n-blue-3 text-sm font-medium text-n-blue-11"
            >
              {{ index + 1 }}
            </span>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-n-slate-12">
                {{ assignmentRuleLabel(rule) }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ assignmentRuleDescription(rule) }}
              </p>
            </div>
            <span
              v-if="index < 3"
              class="i-lucide-lock size-4 shrink-0 text-n-slate-10"
              :aria-label="$t('AGENT_BOTS.ASSIGNMENT_RULES.FIXED_RULE')"
            />
            <div v-else class="flex shrink-0 gap-1">
              <NextButton
                type="button"
                icon="i-lucide-chevron-up"
                slate
                faded
                sm
                :disabled="index === 3"
                :aria-label="$t('AGENT_BOTS.ASSIGNMENT_RULES.MOVE_UP')"
                @click="moveAssignmentRule(index, -1)"
              />
              <NextButton
                type="button"
                icon="i-lucide-chevron-down"
                slate
                faded
                sm
                :disabled="index === formState.assignmentRules.length - 1"
                :aria-label="$t('AGENT_BOTS.ASSIGNMENT_RULES.MOVE_DOWN')"
                @click="moveAssignmentRule(index, 1)"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-3 rounded-xl border border-n-weak p-4">
          <div>
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.CRM.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                $t(
                  crmIntegrationAvailable
                    ? 'AGENT_BOTS.CRM.DESCRIPTION'
                    : 'AGENT_BOTS.CRM.UNAVAILABLE'
                )
              }}
            </p>
          </div>

          <label class="flex items-start gap-3">
            <Checkbox
              v-model="formState.crmEnabled"
              :disabled="!crmIntegrationAvailable"
            />
            <span>
              <span class="block text-sm font-medium text-n-slate-12">
                {{ $t('AGENT_BOTS.CRM.ENABLED') }}
              </span>
            </span>
          </label>

          <div class="ml-7 flex flex-col gap-3">
            <label class="flex items-start gap-3">
              <Checkbox
                v-model="formState.crmCreateLeads"
                :disabled="!crmIntegrationAvailable || !formState.crmEnabled"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('AGENT_BOTS.CRM.CREATE_LEADS') }}
              </span>
            </label>
            <label class="flex items-start gap-3">
              <Checkbox
                v-model="formState.crmReassignExisting"
                :disabled="!crmIntegrationAvailable || !formState.crmEnabled"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('AGENT_BOTS.CRM.REASSIGN_EXISTING') }}
              </span>
            </label>
            <label class="flex items-start gap-3">
              <Checkbox
                v-model="formState.crmSyncSummary"
                :disabled="!crmIntegrationAvailable || !formState.crmEnabled"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('AGENT_BOTS.CRM.SYNC_SUMMARY') }}
              </span>
            </label>
            <label class="flex items-start gap-3">
              <Checkbox
                v-model="formState.crmReassignUnattended"
                :disabled="!crmIntegrationAvailable || !formState.crmEnabled"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('AGENT_BOTS.CRM.REASSIGN_UNATTENDED') }}
              </span>
            </label>
            <label class="flex items-start gap-3">
              <Checkbox
                v-model="formState.crmVtigerOwnerAuthoritative"
                :disabled="!crmIntegrationAvailable || !formState.crmEnabled"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('AGENT_BOTS.CRM.VTIGER_OWNER_AUTHORITATIVE') }}
              </span>
            </label>
            <label class="flex items-start gap-3">
              <Checkbox
                v-model="formState.crmInitialDeliveryOnly"
                :disabled="!crmIntegrationAvailable || !formState.crmEnabled"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('AGENT_BOTS.CRM.INITIAL_DELIVERY_ONLY') }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div
        v-if="botSecret && type === MODAL_TYPES.EDIT"
        class="flex flex-col gap-1"
      >
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.SECRET.LABEL') }}
        </label>
        <AccessToken
          :value="botSecret"
          @on-copy="onCopySecret"
          @on-reset="onResetSecret"
        />
      </div>

      <div v-if="showAccessTokenInput" class="flex flex-col gap-1">
        <label
          v-if="type === MODAL_TYPES.EDIT"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ $t('AGENT_BOTS.ACCESS_TOKEN.TITLE') }}
        </label>
        <AccessToken
          v-if="type === MODAL_TYPES.EDIT"
          :value="accessToken"
          @on-copy="onCopyToken"
          @on-reset="onResetToken"
        />
        <AccessToken
          v-else
          :value="accessToken"
          :show-reset-button="false"
          @on-copy="onCopyToken"
        />
      </div>

      <div
        v-if="botSecret && showAccessToken && type === MODAL_TYPES.CREATE"
        class="flex flex-col gap-1"
      >
        <p class="text-sm text-n-slate-11">
          {{ $t('AGENT_BOTS.SECRET.CREATED_DESC') }}
        </p>
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.SECRET.LABEL') }}
        </label>
        <AccessToken
          :value="botSecret"
          :show-reset-button="false"
          @on-copy="onCopySecret"
        />
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="onClickClose()"
        />
        <NextButton
          v-if="!showAccessToken"
          type="submit"
          data-testid="label-submit"
          :label="confirmButtonLabel"
          :is-loading="isLoading"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
